import InfoIcon from '@mui/icons-material/Info';
import { Card, CardContent, Grid, IconButton, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { IAutomatedEmailCampaignListPayload } from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  FieldOption,
  getDateUsingFormat,
  WsDataGrid,
  WsListingLoader,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard';
import { WsBreadCrumbs } from '../../../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../../../context';
import {
  ChipIconVariants,
  StatusIconChip,
} from '../../../../features/automatedEmailCampaignSettings';
import SearchBar from '../../../../features/teams/components/SearchBarWrapper';
import WsLink from '../../../../features/teams/components/WsLink';
import useGetEmailCampaignLogs, { IEmailCampaignLog } from '../../hooks/useGetEmailCampaignLogs';
import {
  IJobCampaignEmailTabTypes,
  JobEmailCamapignBreadCrumbs,
  defaultColumnSettings,
} from '../../util/JobCampaignEmailHelper';
import { JobInfoDialog } from '../jobInfoDialog';

export const CampaignEmails = () => {
  const methods = useForm();
  const navigate = useNavigate();

  const workspace = useWorkspaceContext();
  const [search, setSearch] = useState<IAutomatedEmailCampaignListPayload>({
    page: 0,
    size: 20,
    search: '',
    workspaceId: workspace?.id ?? '',
    sortField: 'created_on',
    campaignCode: '',
    jobId: '',
  } as IAutomatedEmailCampaignListPayload);
  const { emailCampaignList, loading } = useGetEmailCampaignLogs(search);
  const [settings, setSettings] = useState<FieldOption[]>(defaultColumnSettings);
  const onColumnSettingsReset = () => setSettings(defaultColumnSettings);

  const handlePageChange = (newPage: number) => setSearch({ ...search, page: newPage });

  const handlePageSizeChange = (newPageSize: number) =>
    setSearch({ ...search, size: newPageSize, page: 0 });

  const data = useMemo(
    () =>
      emailCampaignList.content?.map((emailCampaign) => ({
        id: emailCampaign.campaignCode,
        job: `${emailCampaign.jobTitle} (${emailCampaign.jobCode})`,
        createdOn: getDateUsingFormat(emailCampaign.createdOn.toString(), 'DD MMM, YYYY'),
        status: emailCampaign.status,
        sent: emailCampaign.count,
        response: emailCampaign.responseCount ?? 0,
        campaigncode: emailCampaign.campaignCode,
      })) ?? [],
    [emailCampaignList]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'job',
        headerName: 'Job',
        sortable: false,
        flex: 1,
        renderCell: (params) => {
          const rowIndex = params.api.getRowIndex(params.id);
          const emailCampaign = emailCampaignList?.content[rowIndex] ?? ({} as IEmailCampaignLog);
          return (
            <Grid
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: '100%' }}
            >
              <Grid item>
                <WsLink
                  to={`/jobs/job-email-campaign?jobId=${emailCampaign?.jobId}&campaignCode=${emailCampaign?.campaignCode}`}
                >
                  <WsTypography hoverText={params.value} variant={WsTypoVariants.SUB_TITLE}>
                    {params.value}
                  </WsTypography>
                </WsLink>
              </Grid>
              <Grid item>
                <JobInfoDialog emailCampaign={emailCampaign} />
              </Grid>
            </Grid>
          );
        },
      },
      {
        field: 'campaigncode',
        headerName: 'Campaign Name',
        sortable: false,
        flex: 1,
        renderCell: (params) => <WsTypography bold>{params.value}</WsTypography>,
      },
      {
        field: 'sent',
        headerName: 'Sent',
        sortable: false,
        flex: 0.5,
        renderCell: (params) => {
          const rowIndex = params.api.getRowIndex(params.id);
          const emailCampaign = emailCampaignList?.content[rowIndex] ?? ({} as IEmailCampaignLog);
          return (
            <WsTypography
              bold
              color={WsTypoColors.PRIMARY600}
              cursor="pointer"
              onClick={() =>
                navigate(
                  `/jobs/job-email-campaign?jobId=${emailCampaign?.jobId}&campaignCode=${emailCampaign?.campaignCode}&type=${IJobCampaignEmailTabTypes.SENT}`
                )
              }
            >
              {params.value}
            </WsTypography>
          );
        },
      },
      {
        field: 'response',
        headerName: 'Responses',
        sortable: false,
        flex: 0.5,
        renderCell: (params) => {
          const rowIndex = params.api.getRowIndex(params.id);
          const emailCampaign = emailCampaignList?.content[rowIndex] ?? ({} as IEmailCampaignLog);
          return (
            <WsTypography
              bold
              color={WsTypoColors.PRIMARY600}
              cursor="pointer"
              onClick={() =>
                navigate(
                  `/jobs/job-email-campaign?jobId=${emailCampaign?.jobId}&campaignCode=${emailCampaign?.campaignCode}&type=${IJobCampaignEmailTabTypes.RESPONSE}`
                )
              }
            >
              {params.value}
            </WsTypography>
          );
        },
      },
      {
        field: 'createdOn',
        headerName: 'Date Created',
        sortable: false,
        flex: 1,
        renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
      },
      {
        field: 'status',
        headerName: 'Campaign Status',
        sortable: false,
        flex: 1,
        renderCell: (params) => (
          <StatusIconChip
            label={params.value === 'queued' ? 'Sending' : 'Sent'}
            variant={params.value === 'queued' ? ChipIconVariants.QUEUED : ChipIconVariants.SENT}
          />
        ),
      },
    ],
    [emailCampaignList?.content, navigate]
  );

  return (
    <Grid>
      <WsBreadCrumbs breadCrumbsList={JobEmailCamapignBreadCrumbs.CAMPAIGN_EMAILS} />
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Grid container overflow="hidden" rowGap={2}>
            <Grid item xs={12} display="flex" alignItems="center" gap={1}>
              <Grid item>
                <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
                  Email Campaigns
                </WsTypography>
              </Grid>
              <Grid item>
                <Tooltip title="">
                  <IconButton sx={{ p: 0 }}>
                    <InfoIcon sx={{ height: 20, width: 20 }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <FormProvider {...methods}>
                <SearchBar name="query" />
              </FormProvider>
            </Grid>
            <Grid item xs={12}>
              {loading ? (
                <WsListingLoader minHeight={400} />
              ) : (
                <>
                  {emailCampaignList.totalElements ? (
                    <WsDataGrid
                      autoHeight
                      rows={data}
                      columns={columns}
                      paginationMode="server"
                      page={search.page ?? 0}
                      onPageChange={handlePageChange}
                      pageSize={search.size ?? 20}
                      onPageSizeChange={handlePageSizeChange}
                      totalRows={emailCampaignList.totalElements}
                      sortingMode="server"
                      columnVisibility
                      columnSettings={settings}
                      onColumnSettingsChange={(data) => setSettings(data)}
                      onColumnSettingsReset={onColumnSettingsReset}
                    />
                  ) : (
                    <NoDataFoundCard text="No results found" icon={<EmptyTeamsAndUsersIcon />} />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};
